import { useEffect, useState } from "react";

import { targetArea } from "../domain/exerciseMeta";
import { howSteps, moveById, position } from "../domain/moves";
import type { Exercise, ExerciseKind } from "../domain/types";
import { ActionBar } from "./ActionBar";
import { I } from "./icons";
import { MoveFigure } from "./MoveFigure";
import { NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";
import { Tag } from "./Tag";
import "./ExerciseSheet.css";

/** Where a move happens, as the sheet's position tag says it. */
const PLACE: Record<string, string> = {
  chair: "on a chair",
  support: "at the counter",
  standing: "standing",
  wall: "at a wall",
  bed: "in bed",
};

export const KIND_LABEL: Record<ExerciseKind, string> = {
  legs: "legs",
  balance: "balance",
  core: "core",
  upper: "upper",
  cardio: "cardio",
  mobility: "mobility",
  breath: "breath",
};

/** "chair · thighs & glutes": where, then what it works, as a row's second line. */
export function moveWhere(ex: Exercise): string {
  const area = targetArea(ex.id);
  return `${ex.w}${area ? ` · ${area.toLowerCase()}` : ""}`;
}

function videoRow(ex: Exercise): { title: string; sub: string; url: string } {
  if (ex.video) {
    const host = new URL(ex.video).hostname.replace(/^www\./, "");
    const nhs = host.endsWith("nhs.uk");
    return {
      title: nhs ? "Watch the NHS video" : "Watch the Otago video",
      sub: nhs ? `${host} · opens in the browser` : `${host} · the Otago programme's own clip`,
      url: ex.video,
    };
  }
  return { title: "Search YouTube", sub: "a search — pick a physio's or trainer's channel", url: ex.yt };
}

/**
 * How to do one move: its Hindi or yoga name, its kind and place, the dose, the how-to as steps,
 * the common mistakes, why it helps, the easier and harder moves (each opens here), and a video.
 * Opened from the workout player's info button, the session list and the library. (ADR-051)
 */
export function ExerciseSheet({
  ex: opened,
  hipSafe,
  dose,
  note,
  onClose,
}: {
  ex: Exercise;
  hipSafe: boolean;
  /** the library's line: "Not in today's session · start at 5 reps, then 10, then 12" */
  dose?: (ex: Exercise) => string | null;
  /** a filled template's words for the opened move: its support cue and why it differs (ADR-052) */
  note?: string;
  onClose: () => void;
}) {
  // Easier and Harder open their move in this same sheet; the opened one is where it starts.
  const [id, setId] = useState(opened.id);
  useEffect(() => setId(opened.id), [opened.id]);
  const ex = moveById(id) ?? opened;
  const pos = position(ex);
  const steps = howSteps(ex.how);
  const doseLine = dose ? dose(ex) : null;
  const names = [ex.yoga, ex.hi].filter(Boolean).join(" · ");
  const video = videoRow(ex);
  const easier = ex.easier ? moveById(ex.easier) : undefined;
  const harder = ex.harder ? moveById(ex.harder) : undefined;

  return (
    <Sheet
      title={ex.n}
      sub={
        <>
          {names ? (
            <span className="k-ex__names" lang={ex.hi ? "hi" : undefined}>
              {names}
            </span>
          ) : null}
          {moveWhere(ex)}
          {ex.hip && hipSafe ? (
            <>
              {" · "}
              <span className="hip">watch hip</span>
            </>
          ) : null}
        </>
      }
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: "Close", onClick: onClose }} />}
    >
      <div className="k-ex__top">
        <div className="k-ex__tags">
          <Tag tone="neutral">{KIND_LABEL[ex.t]}</Tag>
          {pos ? <Tag tone="neutral">{PLACE[pos]}</Tag> : null}
        </div>
        <MoveFigure id={ex.id} />
      </div>

      {doseLine ? (
        <>
          <h3 className="k-group__h">Dose</h3>
          <p className="k-ex__p">{doseLine}</p>
        </>
      ) : null}

      {note && id === opened.id ? (
        <>
          <h3 className="k-group__h">For you</h3>
          <p className="k-ex__p">{note.charAt(0).toUpperCase() + note.slice(1)}</p>
        </>
      ) : null}

      <h3 className="k-group__h">How to</h3>
      {steps.length > 1 ? (
        <ol className="k-ex__steps">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      ) : (
        <p className="k-ex__p">{ex.how}</p>
      )}

      <h3 className="k-group__h">Common mistakes</h3>
      <ul className="k-ex__steps">
        {ex.wrong.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>

      {ex.why ? (
        <>
          <h3 className="k-group__h">Why</h3>
          <p className="k-ex__p k-ex__why">{ex.why.charAt(0).toUpperCase() + ex.why.slice(1)}.</p>
        </>
      ) : null}

      {easier || harder ? (
        <RowGroup ariaLabel="Easier and harder">
          {easier ? (
            <NavRow
              icon={I[easier.t]}
              title={`Easier · ${easier.n}`}
              subtitle={moveWhere(easier)}
              onClick={() => setId(easier.id)}
            />
          ) : null}
          {harder ? (
            <NavRow
              icon={I[harder.t]}
              title={`Harder · ${harder.n}`}
              subtitle={moveWhere(harder)}
              onClick={() => setId(harder.id)}
            />
          ) : null}
        </RowGroup>
      ) : null}

      <RowGroup ariaLabel="Video">
        <NavRow
          icon={I.ext}
          title={video.title}
          subtitle={video.sub}
          onClick={() => window.open(video.url, "_blank", "noopener,noreferrer")}
        />
      </RowGroup>
    </Sheet>
  );
}
